import React, { Fragment, useEffect, useState, useRef, useCallback } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Divider, Grid, InputAdornment, Stack, TextField, Typography, IconButton, Avatar, Modal, IconButton as MuiIconButton } from "@mui/material";
import { Close, FiberManualRecord, Search, Person, Download, ZoomIn, ZoomOut, RotateRight, Close as CloseIcon, AttachFile, InsertDriveFile, PictureAsPdf, Image as ImageIcon } from "@mui/icons-material";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import { formatMessageTime, serviceOptions, UserTypeId } from "../../common/const";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import moment from "moment";
import CommonButton from "../../common/components/CommonButton";
import { getConversationList, getMessages, sendMessage } from "../../api/chatApi";
import socket from "../../socket";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { useLocation } from "react-router-dom";

const Chat = ({ fromMentor }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { loading, user } = useSelector((state) => state.auth);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [chatList, setChatList] = useState([]);
    const [filteredChatList, setFilteredChatList] = useState([]);
    const [message, setMessage] = useState('');
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [imageUrls, setImageUrls] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const [attachmentUrls, setAttachmentUrls] = useState({});
    const [attachmentErrors, setAttachmentErrors] = useState({});
    const [pageNo, setPageNo] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const pageSize = 20;

    // Image viewer states
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [rotation, setRotation] = useState(0);

    // File upload states
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const fileInputRef = useRef(null);

    // Refs
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const prevMessagesLengthRef = useRef(0);
    const searchTimeoutRef = useRef(null);
    const selectedFilesRef = useRef(null);
    const markReadTimeoutRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Auto mark read function
    const markConversationAsRead = useCallback(() => {
        if (conversationId && conversation?.unread_count > 0) {
            // Clear any existing timeout
            if (markReadTimeoutRef.current) {
                clearTimeout(markReadTimeoutRef.current);
            }

            // Set a small delay to avoid multiple emissions
            markReadTimeoutRef.current = setTimeout(() => {
                socket.emit("markRead", {
                    conversation_id: conversationId,
                    user_id: user?.user_id
                });

                // Update local chat list to reflect read status
                setChatList(prev => prev.map(chat => {
                    if (chat.conversation_id === conversationId) {
                        return { ...chat, unread_count: 0 };
                    }
                    return chat;
                }));

                setFilteredChatList(prev => prev.map(chat => {
                    if (chat.conversation_id === conversationId) {
                        return { ...chat, unread_count: 0 };
                    }
                    return chat;
                }));
            }, 1000);
        }
    }, [conversationId, conversation?.unread_count, user?.user_id]);

    // Auto mark read when conversation is opened or messages are received
    useEffect(() => {
        if (conversationId && conversation) {
            markConversationAsRead();
        }

        return () => {
            if (markReadTimeoutRef.current) {
                clearTimeout(markReadTimeoutRef.current);
            }
        };
    }, [conversationId, conversation, markConversationAsRead]);

    // Also mark as read when user scrolls/views messages
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && conversationId) {
                markConversationAsRead();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [conversationId, markConversationAsRead]);

    // Scroll to selected files function
    const scrollToSelectedFiles = useCallback(() => {
        if (selectedFilesRef.current && selectedFiles.length > 0) {
            requestAnimationFrame(() => {
                selectedFilesRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            });
        }
    }, [selectedFiles.length]);

    useEffect(() => {
        // Scroll to bottom on new messages or when conversation changes
        if (messages.length > prevMessagesLengthRef.current) {
            scrollToBottom();
        }
        prevMessagesLengthRef.current = messages.length;
    }, [messages, scrollToBottom]);

    // Auto scroll to selected files when they change
    useEffect(() => {
        if (selectedFiles.length > 0) {
            scrollToSelectedFiles();
        }
    }, [selectedFiles.length, scrollToSelectedFiles]);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(prev => [...prev, ...files]);

        // Clear the input value so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => {
            const newFiles = prev.filter((_, i) => i !== index);
            return newFiles;
        });
    };

    const handleSendMessage = async () => {
        if (message.trim() || selectedFiles.length > 0) {
            if (selectedFiles.length > 0) {
                const formData = new FormData();
                selectedFiles.map((a) => {
                    formData.append('attachments', a);
                });
                formData.append('conversation_id', conversationId);
                formData.append('message_text', message);
                formData.append('sender_role', user?.user_type_id === UserTypeId.Mentor ? "MENTOR" : "STUDENT");
                await sendMessage(formData);
            } else {
                socket.emit("sendMessage", {
                    conversation_id: conversationId,
                    sender_id: user?.user_id,
                    sender_role: user?.user_type_id === UserTypeId.Mentor ? "MENTOR" : "STUDENT",
                    message_text: message
                });
            }

            setMessage('');
            setSelectedFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Scroll to bottom after sending message
            setTimeout(scrollToBottom, 100);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const getChatList = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getConversationList();

            // Sort chat list by last message time (most recent first)
            const sortedList = response?.data?.sort((a, b) =>
                moment(b.last_message_time).valueOf() - moment(a.last_message_time).valueOf()
            ) || [];

            if (searchParams?.get('id')) {
                const foundConversation = sortedList?.find((a) =>
                    a.mentor?.user_id === searchParams?.get('id') ||
                    a.student?.user_id === searchParams?.get('id')
                );
                setConversation(foundConversation);
                setConversationId(foundConversation?.conversation_id);
            }
            setChatList(sortedList);
            setFilteredChatList(sortedList);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message ||
                err.response?.data?.message ||
                err?.response?.data?.error;
            showNotification(errorMessage, 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    // Debounce search text
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchText]);

    // Filter chat list based on debounced search text
    useEffect(() => {
        if (!chatList.length) return;

        if (debouncedSearchText.trim() === '') {
            setFilteredChatList(chatList);
        } else {
            const searchLower = debouncedSearchText.toLowerCase();
            const filtered = chatList.filter(chat => {
                const fullName = `${chat?.mentor?.first_name || chat?.student?.first_name} ${chat?.mentor?.last_name || chat?.student?.last_name}`.toLowerCase();
                const lastMessage = chat?.last_message?.toLowerCase() || '';
                const serviceOffers = chat?.mentor?.mentorProfile?.service_offers
                    ?.map(value => {
                        const option = serviceOptions?.find(opt => opt.value === value);
                        return option ? option.label.toLowerCase() : value.toLowerCase();
                    })
                    .join(' ') || '';

                return fullName.includes(searchLower) ||
                    lastMessage.includes(searchLower) ||
                    serviceOffers.includes(searchLower);
            });
            setFilteredChatList(filtered);
        }
    }, [debouncedSearchText, chatList]);

    const handleImageError = (userId) => {
        setImageErrors(prev => ({
            ...prev,
            [userId]: true
        }));
    };

    const handleAttachmentError = (blobName) => {
        setAttachmentErrors(prev => ({
            ...prev,
            [blobName]: true
        }));
    };

    // Initial load of chat list
    useEffect(() => {
        getChatList();
    }, []);

    const getMessageList = async (page = 1, isLoadMore = false) => {
        if (!conversationId) return;

        try {
            if (isLoadMore) {
                setIsLoadingMore(true);
            } else {
                dispatch(setLoading({ loading: true }));
            }

            const response = await getMessages(conversationId, page, pageSize);
            const newMessages = response?.data || [];

            // Check if we have more messages to load
            setHasMoreMessages(newMessages.length === pageSize);

            if (isLoadMore) {
                // For loading more, prepend older messages and keep existing ones
                setMessages(prev => [...newMessages, ...prev]);
            } else {
                // For initial load, set messages (they should come in ASC order from API)
                setMessages(newMessages);
                // Scroll to bottom on initial load
                setTimeout(scrollToBottom, 100);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message ||
                err.response?.data?.message ||
                err?.response?.data?.error;
            showNotification(errorMessage, 'error');
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                dispatch(setLoading({ loading: false }));
            }
        }
    };

    useEffect(() => {
        if (conversationId) {
            // Reset messages and page when conversation changes
            setMessages([]);
            setPageNo(1);
            setHasMoreMessages(true);
            setSelectedFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            getMessageList(1, false);
        }
    }, [conversationId]);

    useEffect(() => {
        if (conversationId) {
            socket.emit('joinConversation', conversationId);
        }
    }, [conversationId]);

    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (container && hasMoreMessages && !isLoadingMore) {
            // If scrolled near top (within 50px of top), load more messages
            if (container.scrollTop < 50) {
                const nextPage = pageNo + 1;
                setPageNo(nextPage);
                getMessageList(nextPage, true);
            }
        }

        // Mark as read when user scrolls (if there are unread messages)
        if (conversation?.unread_count > 0) {
            markConversationAsRead();
        }
    }, [hasMoreMessages, isLoadingMore, pageNo, conversation?.unread_count, markConversationAsRead]);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Load profile images
    useEffect(() => {
        if (chatList?.length > 0) {
            const usersWithAvatars = chatList
                .filter(chat => (chat.mentor?.avatar || chat?.student?.avatar))
                .map(chat => ({
                    userId: chat?.mentor?.user_id || chat?.student?.user_id,
                    avatarPath: chat?.mentor?.avatar || chat.student?.avatar
                }));

            const loadAllImages = async () => {
                const imagePromises = usersWithAvatars.map(async (user) => {
                    if (!imageUrls[user.userId] && !imageErrors[user.userId]) {
                        const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${user.avatarPath}&download=false`;
                        try {
                            const response = await fetch(fullUrl, {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                            });

                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            const blob = await response.blob();
                            const objectUrl = URL.createObjectURL(blob);

                            return {
                                userId: user.userId,
                                url: objectUrl
                            };
                        } catch (error) {
                            console.error('Error loading image:', error);
                            return null;
                        }
                    }
                    return null;
                });

                const results = await Promise.all(imagePromises);
                const newImageUrls = results
                    .filter(result => result !== null)
                    .reduce((acc, { userId, url }) => {
                        acc[userId] = url;
                        return acc;
                    }, {});

                setImageUrls(prev => ({
                    ...prev,
                    ...newImageUrls
                }));
            };

            loadAllImages();
        }
    }, [chatList]);

    // Load attachment images
    useEffect(() => {
        if (messages?.length > 0) {
            const attachments = messages
                .flatMap(msg => msg.attachment_url || [])
                .filter(att => att && att.blobName && att.mimetype?.startsWith('image/') && !attachmentUrls[att.blobName] && !attachmentErrors[att.blobName]);

            if (attachments.length === 0) return;

            const loadAttachmentImages = async () => {
                const imagePromises = attachments.map(async (attachment) => {
                    const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${attachment.blobName}&download=false`;
                    try {
                        const response = await fetch(fullUrl, {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }

                        const blob = await response.blob();
                        const objectUrl = URL.createObjectURL(blob);

                        return {
                            blobName: attachment.blobName,
                            url: objectUrl
                        };
                    } catch (error) {
                        console.error('Error loading attachment:', error);
                        return null;
                    }
                });

                const results = await Promise.all(imagePromises);
                const newAttachmentUrls = results
                    .filter(result => result !== null)
                    .reduce((acc, { blobName, url }) => {
                        acc[blobName] = url;
                        return acc;
                    }, {});

                setAttachmentUrls(prev => ({
                    ...prev,
                    ...newAttachmentUrls
                }));
            };

            loadAttachmentImages();
        }
    }, [messages]);

    const renderAvatar = (userId, firstName, lastName, size = 45) => {
        if (imageUrls[userId] && !imageErrors[userId]) {
            return (
                <img
                    src={imageUrls[userId]}
                    alt={`${firstName} ${lastName}`}
                    width={`${size}px`}
                    height={`${size}px`}
                    style={{ borderRadius: 10, objectFit: 'cover' }}
                    onError={() => handleImageError(userId)}
                />
            );
        } else {
            const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
            return (
                <Avatar
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: 1,
                        bgcolor: theme.palette.primary.main,
                        fontSize: size * 0.4,
                        fontWeight: 'bold'
                    }}
                >
                    {initials || <Person />}
                </Avatar>
            );
        }
    };

    useEffect(() => {
        const handleReceiveMessage = (message) => {
            // Only add message if it belongs to current conversation
            if (message.conversation_id === conversationId) {
                setMessages((prev) => {
                    // Check if message already exists to prevent duplicates
                    const exists = prev.some(m => m.message_id === message.message_id);
                    if (exists) return prev;
                    return [...prev, message];
                });

                // Auto mark as read if conversation is open
                if (message.sender_id !== user?.user_id) {
                    markConversationAsRead();
                }
            }

            // Update chat list last message
            setChatList(prev => {
                const updatedList = prev.map(chat => {
                    if (chat.conversation_id === message.conversation_id) {
                        // Check if message has attachment
                        const hasAttachment = message.attachment_url && message.attachment_url.length > 0;
                        const lastMessageText = hasAttachment
                            ? `📎 ${message.attachment_url.length} attachment(s)`
                            : message.message_text;

                        return {
                            ...chat,
                            last_message: lastMessageText,
                            last_message_time: message.created_at,
                            unread_count: message.sender_id !== user?.user_id && message.conversation_id !== conversationId ? (chat.unread_count || 0) + 1 : chat.unread_count
                        };
                    }
                    return chat;
                }).sort((a, b) =>
                    moment(b.last_message_time).valueOf() - moment(a.last_message_time).valueOf()
                );

                // Also update filtered list based on current search
                if (debouncedSearchText.trim() !== '') {
                    const searchLower = debouncedSearchText.toLowerCase();
                    setFilteredChatList(updatedList.filter(chat => {
                        const fullName = `${chat?.mentor?.first_name || chat?.student?.first_name} ${chat?.mentor?.last_name || chat?.student?.last_name}`.toLowerCase();
                        const lastMessage = chat?.last_message?.toLowerCase() || '';
                        return fullName.includes(searchLower) || lastMessage.includes(searchLower);
                    }));
                } else {
                    setFilteredChatList(updatedList);
                }

                return updatedList;
            });
        };

        socket.on("receiveMessage", handleReceiveMessage);

        return () => {
            socket.off("receiveMessage", handleReceiveMessage);
        };
    }, [conversationId, user?.user_id, debouncedSearchText, markConversationAsRead]);

    // Sort messages by created_at in ascending order (oldest first, newest last)
    const sortedMessages = [...messages]
        .filter(msg => msg.conversation_id === conversationId)
        .sort((a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf());

    // Clear search function
    const handleClearSearch = () => {
        setSearchText('');
        setDebouncedSearchText('');
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    // Handle file download for all file types
    const handleFileDownload = async (blobName, filename, mimetype) => {
        try {
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${blobName}&download=true`;

            const response = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Get the blob from response
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'download';
            document.body.appendChild(a);
            a.click();

            // Cleanup
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            showNotification('File downloaded successfully', 'success');
        } catch (error) {
            console.error('Error downloading file:', error);
            showNotification('Failed to download file', 'error');
        }
    };

    // Handle image download
    const handleImageDownload = async (imageUrl, filename) => {
        try {
            const response = await fetch(imageUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'image.jpg';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            showNotification('Image downloaded successfully', 'success');
        } catch (error) {
            console.error('Error downloading image:', error);
            showNotification('Failed to download image', 'error');
        }
    };

    // Handle image click for viewer
    const handleImageClick = (imageUrl, filename) => {
        setSelectedImage({ url: imageUrl, filename });
        setImageViewerOpen(true);
        setZoomLevel(1);
        setRotation(0);
    };

    // Image viewer controls
    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation(prev => (prev + 90) % 360);

    // Get appropriate icon based on file type
    const getFileIcon = (mimetype, isSent) => {
        const iconStyle = {
            fontSize: 30,
            color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)'
        };

        if (mimetype?.startsWith('image/')) {
            return <ImageIcon sx={iconStyle} />;
        } else if (mimetype === 'application/pdf') {
            return <PictureAsPdf sx={iconStyle} />;
        } else {
            return <InsertDriveFile sx={iconStyle} />;
        }
    };

    // Render attachments
    const renderAttachments = (attachments, isSent) => {
        if (!attachments || attachments.length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                {attachments.map((attachment, index) => {
                    const isImage = attachment.mimetype?.startsWith('image/');
                    const isVideo = attachment.mimetype?.startsWith('video/');
                    const isAudio = attachment.mimetype?.startsWith('audio/');

                    if (isImage) {
                        return (
                            <Box key={index} sx={{ mb: 1 }}>
                                {attachmentUrls[attachment.blobName] && !attachmentErrors[attachment.blobName] ? (
                                    <img
                                        src={attachmentUrls[attachment.blobName]}
                                        alt={attachment.filename}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '300px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            objectFit: 'cover',
                                            border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                        onClick={() => handleImageClick(attachmentUrls[attachment.blobName], attachment.filename)}
                                        onError={() => handleAttachmentError(attachment.blobName)}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1,
                                            bgcolor: isSent ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: isSent ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'
                                            }
                                        }}
                                        onClick={() => handleFileDownload(attachment.blobName, attachment.filename, attachment.mimetype)}
                                    >
                                        {getFileIcon(attachment.mimetype, isSent)}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: isSent ? 'black' : 'white' }}>
                                                {attachment.filename}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }}>
                                                {formatFileSize(attachment.size)}
                                            </Typography>
                                        </Box>
                                        <Download sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }} />
                                    </Box>
                                )}
                            </Box>
                        );
                    } else if (isVideo) {
                        return (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1,
                                        bgcolor: isSent ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: isSent ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'
                                        }
                                    }}
                                    onClick={() => handleFileDownload(attachment.blobName, attachment.filename, attachment.mimetype)}
                                >
                                    {getFileIcon(attachment.mimetype, isSent)}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: isSent ? 'black' : 'white' }}>
                                            {attachment.filename}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }}>
                                            {formatFileSize(attachment.size)}
                                        </Typography>
                                    </Box>
                                    <Download sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }} />
                                </Box>
                                <video
                                    controls
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        marginTop: '8px'
                                    }}
                                >
                                    <source src={`${process.env.REACT_APP_BASE_URL}/file?bname=${attachment.blobName}&download=false`} type={attachment.mimetype} />
                                    Your browser does not support the video tag.
                                </video>
                            </Box>
                        );
                    } else if (isAudio) {
                        return (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1,
                                        bgcolor: isSent ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: isSent ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'
                                        }
                                    }}
                                    onClick={() => handleFileDownload(attachment.blobName, attachment.filename, attachment.mimetype)}
                                >
                                    {getFileIcon(attachment.mimetype, isSent)}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: isSent ? 'black' : 'white' }}>
                                            {attachment.filename}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }}>
                                            {formatFileSize(attachment.size)}
                                        </Typography>
                                    </Box>
                                    <Download sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }} />
                                </Box>
                                <Box sx={{
                                    bgcolor: isSent ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                    p: 1,
                                    borderRadius: 1,
                                    minWidth: '250px',
                                    mt: 1
                                }}>
                                    <audio controls style={{ width: '100%' }}>
                                        <source src={`${process.env.REACT_APP_BASE_URL}/file?bname=${attachment.blobName}&download=false`} type={attachment.mimetype} />
                                        Your browser does not support the audio tag.
                                    </audio>
                                </Box>
                            </Box>
                        );
                    } else {
                        return (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1,
                                        bgcolor: isSent ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: isSent ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'
                                        }
                                    }}
                                    onClick={() => handleFileDownload(attachment.blobName, attachment.filename, attachment.mimetype)}
                                >
                                    {getFileIcon(attachment.mimetype, isSent)}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: isSent ? 'black' : 'white' }}>
                                            {attachment.filename}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }}>
                                            {formatFileSize(attachment.size)}
                                        </Typography>
                                    </Box>
                                    <Download sx={{ color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)' }} />
                                </Box>
                            </Box>
                        );
                    }
                })}
            </Box>
        );
    };

    return (
        <Fragment>
            {loading && <LoadingSpinner />}

            {/* Image Viewer Modal */}
            <Modal
                open={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.9)'
                }}
            >
                <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                    <Box sx={{ position: 'absolute', top: -50, right: 0, display: 'flex', gap: 1, zIndex: 1 }}>
                        <MuiIconButton onClick={handleZoomOut} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                            <ZoomOut />
                        </MuiIconButton>
                        <MuiIconButton onClick={handleZoomIn} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                            <ZoomIn />
                        </MuiIconButton>
                        <MuiIconButton onClick={handleRotate} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                            <RotateRight />
                        </MuiIconButton>
                        <MuiIconButton onClick={() => handleImageDownload(selectedImage?.url, selectedImage?.filename)} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                            <Download />
                        </MuiIconButton>
                        <MuiIconButton onClick={() => setImageViewerOpen(false)} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                            <CloseIcon />
                        </MuiIconButton>
                    </Box>
                    {selectedImage && (
                        <Box
                            component="img"
                            src={selectedImage.url}
                            alt={selectedImage.filename}
                            sx={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    )}
                </Box>
            </Modal>

            <PageHeader
                title={fromMentor ? "Communication Hub" : "Chat"}
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                            {fromMentor ? 'Mentors' : 'My DCP Services'}
                        </Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                            {fromMentor ? 'Communication Hub' : 'Chat'}
                        </Typography>
                    </Stack>
                }
            />
            <Grid container spacing={2}>
                <Grid item md={4} sm={12} xs={12}>
                    <CommonCard
                        borderRadius={1}
                        paddingBottom="0px"
                        border={`1px solid #0000001A`}
                        content={
                            <Fragment>
                                <TextField
                                    placeholder="Search conversations..."
                                    variant="outlined"
                                    size="small"
                                    value={searchText}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            background: theme.palette.background.main,
                                            'fieldset': {
                                                border: 'none'
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: 'none'
                                            },
                                        },
                                        '& input': {
                                            height: '25px',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            width: searchText ? '330px' : '360px'
                                        }
                                    }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ fontSize: 30, color: theme.palette.background.dark }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchText && (
                                            <InputAdornment position="end" onClick={handleClearSearch}>
                                                <Close
                                                    sx={{
                                                        fontSize: 20,
                                                        color: theme.palette.background.dark,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ height: '63vh', overflow: 'auto' }}>
                                    {filteredChatList?.length > 0 ? (
                                        filteredChatList.map((a, index) => (
                                            <CommonCard
                                                key={a.conversation_id || index}
                                                borderRadius={2}
                                                paddingBottom="0px"
                                                background={'#FAFAFA'}
                                                padding="13px"
                                                content={
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            try {
                                                                setConversation(a);
                                                                setConversationId(a.conversation_id);
                                                                setMessage('');
                                                                setSelectedFiles([]);
                                                                if (fileInputRef.current) {
                                                                    fileInputRef.current.value = '';
                                                                }
                                                            } catch (err) {
                                                                console.error('Error selecting conversation:', err);
                                                            }
                                                        }}
                                                    >
                                                        <Box display="flex" gap={1}>
                                                            {renderAvatar(
                                                                a?.mentor?.user_id || a?.student?.user_id,
                                                                a?.mentor?.first_name || a?.student?.first_name,
                                                                a?.mentor?.last_name || a?.student?.last_name,
                                                                45
                                                            )}
                                                            <Box>
                                                                <Typography fontWeight={600} fontSize={16}>
                                                                    {`${a?.mentor?.first_name || a?.student?.first_name} ${a.mentor?.last_name || a?.student?.last_name}`}
                                                                </Typography>
                                                                <Typography color={theme.palette.background.dark} fontSize={13}>
                                                                    {a?.last_message?.length > 100
                                                                        ? `${a.last_message?.slice(0, 100)}....`
                                                                        : a?.last_message}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box>
                                                            <Typography fontSize={14}>
                                                                {a?.last_message_time ? formatMessageTime(a?.last_message_time) : ''}
                                                            </Typography>
                                                            {a.unread_count > 0 && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'relative',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        float: 'right',
                                                                        minWidth: 20,
                                                                        height: 20,
                                                                        padding: '0 3px',
                                                                        borderRadius: 1,
                                                                        backgroundColor: 'red',
                                                                        color: 'white',
                                                                        fontSize: '12px',
                                                                        fontWeight: 600,
                                                                        lineHeight: 1,
                                                                        whiteSpace: 'nowrap',
                                                                        ...(a.unread_count > 99 && {
                                                                            padding: '0 4px',
                                                                            fontSize: '11px',
                                                                        }),
                                                                    }}
                                                                >
                                                                    {a.unread_count > 99 ? '99+' : a.unread_count}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        ))
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                                            <Typography color={theme.palette.background.dark} fontSize={16}>
                                                {searchText ? 'No conversations match your search' : 'No Chat Found'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Fragment>
                        }
                    />
                </Grid>
                {conversation !== null && (
                    <Grid item md={8} sm={12} xs={12}>
                        <CommonCard
                            borderRadius={1}
                            paddingBottom="0px"
                            border={`1px solid #0000001A`}
                            headerContent={
                                <Fragment>
                                    <Box display="flex" gap={1} p={1}>
                                        {renderAvatar(
                                            conversation?.mentor?.user_id || conversation?.student?.user_id,
                                            conversation?.mentor?.first_name || conversation?.student?.first_name,
                                            conversation?.mentor?.last_name || conversation?.student?.last_name,
                                            45
                                        )}
                                        <Box>
                                            <Typography fontWeight={600} fontSize={16}>
                                                {`${conversation?.mentor?.first_name || conversation?.student?.first_name} ${conversation?.mentor?.last_name || conversation?.student?.last_name}`}
                                            </Typography>
                                            <Typography color={theme.palette.background.dark} fontSize={13}>
                                                {conversation?.mentor?.mentorProfile?.service_offers
                                                    ?.map(value => {
                                                        const option = serviceOptions?.find(opt => opt.value === value);
                                                        return option ? option.label : value;
                                                    })
                                                    .join(', ') || 'Student'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                </Fragment>
                            }
                            content={
                                <Box
                                    ref={messagesContainerRef}
                                    onScroll={handleScroll}
                                    sx={{
                                        height: '52.3vh',
                                        overflow: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative'
                                    }}
                                >
                                    {isLoadingMore && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                            <LoadingSpinner size={20} />
                                        </Box>
                                    )}

                                    <Box sx={{ flex: 1 }}>
                                        {sortedMessages?.map((message, index) => {
                                            const isSent = message?.sender_id === user?.user_id;
                                            // Check if this is the last received message (not sent by current user)
                                            const receivedMessages = sortedMessages.filter(m => m?.sender_id !== user?.user_id);
                                            const lastReceivedMessage = receivedMessages[receivedMessages?.length - 1];
                                            const isLastReceivedMessage = message === lastReceivedMessage;

                                            return (
                                                <Box
                                                    key={message.message_id || index}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: isSent ? 'flex-end' : 'flex-start',
                                                        mb: 2,
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: isSent ? 'row-reverse' : 'row',
                                                        alignItems: 'flex-end',
                                                        maxWidth: '70%',
                                                    }}>
                                                        {/* Avatar for last received message only */}
                                                        {isLastReceivedMessage && !isSent && (
                                                            <Box sx={{ mr: 1, mb: 0 }}>
                                                                {renderAvatar(
                                                                    conversation?.mentor?.user_id || conversation?.student?.user_id,
                                                                    conversation?.mentor?.first_name || conversation?.student?.first_name,
                                                                    conversation?.mentor?.last_name || conversation?.student?.last_name,
                                                                    30
                                                                )}
                                                            </Box>
                                                        )}

                                                        {/* Message bubble */}
                                                        <Box
                                                            sx={{
                                                                background: isSent ? '#7A869180' : theme.palette.secondary.main,
                                                                padding: '8px 12px',
                                                                borderRadius: 1.5,
                                                                color: isSent ? 'black' : 'white',
                                                                maxWidth: '100%',
                                                                wordBreak: 'break-word',
                                                                ml: isSent ? 0 : (!isLastReceivedMessage ? 4 : 0),
                                                            }}
                                                        >
                                                            {/* Message text */}
                                                            {message?.message_text && (
                                                                <Typography sx={{
                                                                    fontSize: '15px',
                                                                    display: 'block',
                                                                    mb: message?.attachment_url?.length > 0 ? 1 : 0,
                                                                    whiteSpace: 'pre-wrap',
                                                                }}>
                                                                    {message?.message_text}
                                                                </Typography>
                                                            )}

                                                            {/* Attachments */}
                                                            {renderAttachments(message?.attachment_url, isSent)}

                                                            {/* Timestamp */}
                                                            <Typography
                                                                component="span"
                                                                fontSize="11px"
                                                                sx={{
                                                                    color: isSent ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                                                                    display: 'block',
                                                                    textAlign: 'right',
                                                                    mt: 0.5,
                                                                    opacity: 0.8,
                                                                }}
                                                            >
                                                                {moment(message?.created_at).format('hh:mm A')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    {/* Selected files preview fixed at bottom */}
                                    {selectedFiles.length > 0 && (
                                        <Box
                                            ref={selectedFilesRef}
                                            sx={{
                                                p: 2,
                                                bgcolor: '#f5f5f5',
                                                borderTop: '1px solid #e0e0e0',
                                                borderBottom: '1px solid #e0e0e0',
                                                mt: 'auto',
                                                mb: 1,
                                                animation: 'fadeIn 0.3s ease-in',
                                                '@keyframes fadeIn': {
                                                    '0%': { opacity: 0, transform: 'translateY(10px)' },
                                                    '100%': { opacity: 1, transform: 'translateY(0)' }
                                                },
                                                borderRadius: 1,
                                                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                                                position: 'sticky',
                                                bottom: 0,
                                                zIndex: 10
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: theme.palette.primary.main }}>
                                                Selected files ({selectedFiles.length}):
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {selectedFiles.map((file, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            position: 'relative',
                                                            width: 80,
                                                            height: 80,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.05)',
                                                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                                            }
                                                        }}
                                                    >
                                                        {file.type.startsWith('image/') ? (
                                                            <img
                                                                src={URL.createObjectURL(file)}
                                                                alt={file.name}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                height: '100%',
                                                                bgcolor: '#f0f0f0',
                                                                p: 0.5
                                                            }}>
                                                                {file.type === 'application/pdf' ? (
                                                                    <PictureAsPdf sx={{ fontSize: 30, color: '#d32f2f' }} />
                                                                ) : file.type.startsWith('video/') ? (
                                                                    <ImageIcon sx={{ fontSize: 30, color: '#1976d2' }} />
                                                                ) : file.type.startsWith('audio/') ? (
                                                                    <ImageIcon sx={{ fontSize: 30, color: '#388e3c' }} />
                                                                ) : (
                                                                    <InsertDriveFile sx={{ fontSize: 30, color: '#f57c00' }} />
                                                                )}
                                                                <Typography variant="caption" sx={{
                                                                    fontSize: '8px',
                                                                    textAlign: 'center',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    width: '100%',
                                                                    whiteSpace: 'nowrap',
                                                                    fontWeight: 500
                                                                }}>
                                                                    {file.name.split('.').pop()?.toUpperCase()}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeSelectedFile(index)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                right: 2,
                                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                                p: 0.3,
                                                                '&:hover': {
                                                                    bgcolor: 'white',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                transition: 'transform 0.2s ease'
                                                            }}
                                                        >
                                                            <Close sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                        <Typography variant="caption" sx={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bgcolor: 'rgba(0,0,0,0.7)',
                                                            color: 'white',
                                                            fontSize: '8px',
                                                            textAlign: 'center',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            px: 0.5,
                                                            py: 0.3
                                                        }}>
                                                            {file.name.length > 12 ? file.name.substring(0, 9) + '...' : file.name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    <div ref={messagesEndRef} />
                                </Box>
                            }
                            footerContent={
                                user?.user_type_id !== UserTypeId.Parent && (
                                    <Fragment>
                                        <Box display="flex" flexDirection="column" width="100%" gap={1}>
                                            <Box
                                                sx={{
                                                    backgroundColor: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    width: '100%'
                                                }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    placeholder="Type a message..."
                                                    value={message}
                                                    onChange={(e) => setMessage(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={uploadingFiles}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    ref={fileInputRef}
                                                                    onChange={handleFileSelect}
                                                                    style={{ display: 'none' }}
                                                                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                                                />
                                                                <IconButton
                                                                    onClick={() => fileInputRef.current?.click()}
                                                                    disabled={uploadingFiles}
                                                                    sx={{
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(0,0,0,0.04)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <img src="/Images/Attachment.png" alt="attachment" height="30px" />
                                                                </IconButton>
                                                                <CommonButton
                                                                    label={uploadingFiles ? "Uploading..." : "Send"}
                                                                    marginBottom="0px"
                                                                    fontWeight={600}
                                                                    startIcon={<img src="/Images/Send.png" alt="send" height="15px" />}
                                                                    disabled={(!message.trim() && selectedFiles.length === 0) || uploadingFiles}
                                                                    handleClick={handleSendMessage}
                                                                    height="35px"
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        sx: {
                                                            height: 50,
                                                            backgroundColor: '#f5f5f5',
                                                        }
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            background: theme.palette.background.main,
                                                            'fieldset': {
                                                                border: 'none'
                                                            },
                                                            '&:hover fieldset': {
                                                                border: 'none',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                border: 'none'
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Fragment>)
                            }
                        />
                    </Grid>
                )}
            </Grid>
        </Fragment>
    );
};

export default Chat;